<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxFileUploader } from 'devextreme-vue/file-uploader'

import { useNotify } from '@/composables/notify'

import { formatBytes } from '@/lib/utils'

const props = defineProps({
  cellInfo: {
    type: Object,
  },
  validationError: { type: Object, default: () => ({ message: '' }) },
  isValid: { type: Boolean, default: true },
  options: {
    type: Object,
    default: () => ({
      allow_download: true,
    })
  },
})

const i18n = getI18n()

const { notifyError } = useNotify()

const maxSize = window.config.MAX_UPLOAD_SIZE
const maxFilesNumber = window.config.MAX_FILE_NUMBER
const allowedExtensions = window.config.ALLOWED_ATTACHMENT_EXTENSIONS
const acceptString = allowedExtensions.map(ext => (ext.startsWith('.') ? ext : '.' + ext)).join(',')

const filesList = ref(props.cellInfo.value || [])

const onValueChanged = (e) => {
  if ((filesList.value.length + e.value.length) > maxFilesNumber) { notifyError(i18n.t('common.tooManyFiles', [maxFilesNumber])); return }
  e.value.forEach(file => {
    const extension = file.name.split('.').pop().toLowerCase()

    if (!props.options?.noRestrictions) {
      if (!allowedExtensions.includes(extension)) { notifyError(i18n.t('common.invalidFileType')); return }
      if (file.size > maxSize) { notifyError(i18n.t('common.fileTooLarge', [formatBytes(maxSize)])); return }
    }

    const reader = new FileReader()
    const fileName = file.name
    reader.onload = (args) => {
      filesList.value.push({
        name: fileName,
        payload: args.target.result
      })
      props.cellInfo.setValue(filesList.value)
    }
    if (file instanceof Blob) {
      if (!filesList.value.find(item => item.name === fileName)) {
        reader.readAsDataURL(file) // convert to base64 string
      }
    }
  })
}

const resetFiles = () => {
  filesList.value = []
  props.cellInfo.setValue(filesList.value)
}

const removeFile = (file) => {
  filesList.value = filesList.value.filter(item => item.name !== file.name)
  props.cellInfo.setValue(filesList.value)
}

const emit = defineEmits({
  download: (e) => true,
})

defineExpose({ resetFiles })
</script>
<template>
  <div>
    <dx-file-uploader
      :accept="acceptString"
      :multiple="true"
      :show-file-list="false"
      upload-mode="useForm"
      @value-changed="onValueChanged"
    />
    <q-separator  v-show="filesList.length === 0" :color="props.isValid ? 'gray' : 'negative'" inset />
    <q-scroll-area v-show="filesList.length > 0" style="height: 8rem; overflow: auto; padding-right: 1rem;">
      <q-list
        bordered
        separator
      >
        <q-item
          v-for="item in filesList"
          :key="item.name"
          v-ripple
          clickable
        >
          <q-item-section
            side
            top
            @click="removeFile(item)"
          >
            <q-avatar icon="mdi-delete" />
          </q-item-section>
          <q-item-section> {{ item.name }} </q-item-section>
          <q-item-section
            v-if="props.options?.allow_download"
            side
            top
            @click="emit('download', { value: item })"
          >
            <q-avatar icon="mdi-download" />
          </q-item-section>
        </q-item>
      </q-list>
    </q-scroll-area>
    <div v-show="filesList.length === 0 && !props.options?.noRestrictions" class="q-pa-md">
      <div class="row items-center q-mb-sm">
        <span class="text-caption text-grey-7">
          {{ i18n.t('common.maxFileSize', [formatBytes(maxSize)]) }}
        </span>
      </div>
      <div class="row items-center q-mb-sm">
        <span class="text-caption text-grey-7">
          {{ i18n.t('common.maxFileNumber', [maxFilesNumber]) }}
        </span>
      </div>
      <div class="q-mb-sm">
        <div class="row items-center q-mb-xs">
          <span class="text-caption text-grey-7">
            {{ i18n.t('common.allowedFileTypes') }}
          </span>
        </div>
        <div class="row wrap q-gutter-xs">
          <q-chip
            v-for="ext in allowedExtensions"
            :key="ext"
            size="sm"
            dense
            text-color="grey-8"
            color="grey-2"
          >
            {{ ext }}
          </q-chip>
        </div>
      </div>
    </div>
  </div>
</template>
